import React, { useCallback, useMemo } from "react";
import { Filter as FilterIcon, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateFilter } from "@/components/DateFilter";
import { MultiSelect } from "@/components/MultiSelect";

const _overlap = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.some((x) => b.includes(x));

export const FilterBar = ({ filters, options, onChange, onReset }) => {
  const set = useCallback(
    (patch) => onChange({ ...filters, ...patch }),
    [filters, onChange]
  );

  const onMulti = (key) => (vals) => set({ [key]: vals });

  // Cascade: when verticals are picked, narrow programs and cohorts to those
  // belonging to the selected verticals. Cohorts further narrow by selected
  // programs.
  const programOptions = useMemo(() => {
    const all = options.programs || [];
    if (!filters.vertical?.length) return all;
    return all.filter(
      (p) => !p.vertical_ids?.length || _overlap(p.vertical_ids, filters.vertical)
    );
  }, [options.programs, filters.vertical]);

  const cohortOptions = useMemo(() => {
    const all = options.cohorts || [];
    let scoped = all;
    if (filters.vertical?.length) {
      scoped = scoped.filter(
        (c) => !c.vertical_ids?.length || _overlap(c.vertical_ids, filters.vertical)
      );
    }
    if (filters.program?.length) {
      scoped = scoped.filter(
        (c) => !c.program_ids?.length || _overlap(c.program_ids, filters.program)
      );
    }
    return scoped;
  }, [options.cohorts, filters.vertical, filters.program]);

  const clientTypeOptions = (options.client_types || []).map((c) => ({
    id: c,
    name: c,
  }));

  return (
    <div
      className="flex items-stretch gap-2 sm:card-surface sm:p-3 sm:p-4"
      data-testid="filter-bar"
    >
      <div className="flex-1 min-w-0 flex items-center gap-2 overflow-x-auto no-scrollbar pr-2">
        <div className="flex items-center gap-2 shrink-0 md:pr-2 md:mr-1 md:border-r md:border-border">
          <FilterIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground hidden sm:inline">
            Filters
          </span>
        </div>

        {/* 1. Date */}
        <div className="shrink-0">
          <DateFilter
            value={{
              start_date: filters.start_date,
              end_date: filters.end_date,
            }}
            onChange={({ start_date, end_date }) =>
              set({ start_date, end_date, quarter: null, year: null })
            }
          />
        </div>

        {/* 2. Vertical */}
        <MultiSelect
          placeholder="Vertical"
          options={options.verticals || []}
          value={filters.vertical || []}
          onChange={onMulti("vertical")}
          className="w-[150px] md:w-[170px] shrink-0"
          testId="filter-vertical"
        />

        {/* 3. Program (cascaded by Vertical) */}
        <MultiSelect
          placeholder="Program"
          options={programOptions}
          value={filters.program || []}
          onChange={onMulti("program")}
          className="w-[160px] md:w-[190px] shrink-0"
          testId="filter-program"
        />

        {/* 4. Cohort (cascaded by Vertical + Program) */}
        <MultiSelect
          placeholder="Cohort"
          options={cohortOptions}
          value={filters.cohort || []}
          onChange={onMulti("cohort")}
          className="w-[140px] md:w-[160px] shrink-0"
          testId="filter-cohort"
        />

        {/* 5. Client type */}
        <MultiSelect
          placeholder="Client type"
          options={clientTypeOptions}
          value={filters.client_type || []}
          onChange={onMulti("client_type")}
          className="w-[130px] md:w-[140px] shrink-0"
          testId="filter-client-type"
        />

        {/* 6. Sales person */}
        <MultiSelect
          placeholder="Sales person"
          options={options.sales_people || []}
          value={filters.salesperson || []}
          onChange={onMulti("salesperson")}
          className="w-[150px] md:w-[180px] shrink-0"
          testId="filter-salesperson"
        />
      </div>

      <div className="shrink-0 self-center sticky right-0 sm:static bg-[hsl(var(--bg))] sm:bg-transparent pl-2 -mr-1 sm:mr-0">
        <Button
          variant="outline"
          size="sm"
          onClick={onReset}
          className="h-9 gap-2 border-[#E76F1A]/40 bg-[#E76F1A]/10 hover:bg-[#E76F1A]/20 text-[#B05316] font-semibold"
          data-testid="filter-reset"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Reset filters</span>
          <span className="sm:hidden">Reset</span>
        </Button>
      </div>
    </div>
  );
};
